import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/auth-context'
import { getConfigValues } from '../../lib/remote-config'
import { User } from 'lucide-react'

// Break down demo messages by role
const demoMessages = [
  { 
    role: 'user',
    content: "What are the strongest arguments for and against artificial consciousness?"
  },
  { 
    role: 'protagonist',
    content: `There are several compelling arguments suggesting artificial consciousness is possible:

1. **Computational Complexity**: As AI systems grow more complex, they could potentially reach a level of complexity comparable to the human brain, enabling consciousness to emerge.

2. **Functional Equivalence**: If an AI system can functionally replicate all aspects of human cognition, there's reason to believe consciousness might accompany this functionality.

3. **Substrate Independence**: Consciousness may be substrate-independent, meaning it could arise from any sufficiently complex information processing system, not just biological brains.

4. **Information Integration Theory**: According to this theory, consciousness emerges from integrated information systems with certain causal properties, which advanced AI could potentially satisfy.

5. **Emergent Properties**: Consciousness might be an emergent property of complex systems, which could arise in sufficiently advanced computational networks.`
  },
  { 
    role: 'antagonist',
    content: `There are fundamental challenges to the concept of artificial consciousness:

1. **Hard Problem of Consciousness**: We still don't understand how subjective experience emerges from physical processes. This "explanatory gap" suggests we may be missing fundamental knowledge about consciousness.

2. **Chinese Room Argument**: Searle's thought experiment suggests that computational systems may simulate understanding without genuinely possessing it, merely manipulating symbols according to rules.

3. **Qualia and Phenomenal Experience**: Machines lack the biological structures that create subjective experiential qualities in humans. It's unclear how phenomenal experience could emerge from silicon.

4. **Intentionality**: AI systems receive their meaning and purpose externally from their creators, while consciousness typically involves intrinsic intentionality.

5. **Biological Nature of Consciousness**: Consciousness may be fundamentally biological, arising from specific evolutionary processes and biochemical mechanisms that cannot be replicated in machines.`
  },
  { 
    role: 'evaluator',
    content: `Both sides present compelling arguments about artificial consciousness.

For artificial consciousness, the strongest arguments center around computational complexity, substrate independence, and emergent properties theories. If consciousness emerges from information processing of sufficient complexity, then advanced AI systems could potentially develop consciousness.

Against artificial consciousness, the hard problem of consciousness remains unsolved—we don't understand how subjective experience emerges from physical processes. The Chinese Room argument suggests simulation of understanding isn't actual understanding, and consciousness may be inherently biological.

The debate fundamentally hinges on unresolved questions about consciousness itself. Without a complete understanding of how consciousness arises in humans, it's difficult to determine conclusively whether machines could ever possess it. Additionally, we currently lack reliable methods to detect or measure consciousness in non-human entities.

This remains one of the most profound open questions at the intersection of philosophy, computer science, and neuroscience.`
  }
];

// Create a new component for the message content (similar to ChatInterface)
const MessageContent = ({ content, shouldStream }) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    if (!shouldStream) {
      setDisplayedText(content);
      return;
    }

    // Split by spaces while preserving newlines
    const words = content.split(/(\s+)/);
    let currentWordIndex = 0;

    const interval = setInterval(() => {
      if (currentWordIndex < words.length) {
        // Get a random number of words between 1 and 13 (or remaining words if less)
        const remainingWords = words.length - currentWordIndex;
        const wordsToAdd = Math.min(Math.floor(Math.random() * 13) + 1, remainingWords);
        
        // Add the next batch of words, preserving all whitespace
        const newWords = words.slice(0, currentWordIndex + wordsToAdd).join('');
        setDisplayedText(newWords);
        currentWordIndex += wordsToAdd;
      } else {
        clearInterval(interval);
      }
    }, 90); // Higher slows it down, lower speeds it up

    return () => clearInterval(interval);
  }, [content, shouldStream]);

  return <p className="whitespace-pre-wrap">{displayedText}</p>;
};

export default function LandingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentMessageIndex, setCurrentMessageIndex] = useState(-1) // Start before the first message
  const [streamingMessage, setStreamingMessage] = useState(false)
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false) // New state for loading indicator
  const [monthlyFreeDebates, setMonthlyFreeDebates] = useState("--")

  useEffect(() => {
    const { monthlyFreeDebates } = getConfigValues()
    setMonthlyFreeDebates(monthlyFreeDebates)
  }, [])

  useEffect(() => {
    if (user) {
      navigate('/chat')
    }
  }, [user, navigate])

  // Start the demo conversation
  useEffect(() => {
    const startDemo = async () => {
      setShowLoadingIndicator(true) // Show loading at the beginning
      
      // First display the user message
      setCurrentMessageIndex(0);
      setStreamingMessage(true);
      
      // Wait for first message to complete
      setTimeout(() => {
        setStreamingMessage(false);
        
        // Keep loading indicator visible between messages
        setTimeout(() => {
          // Show protagonist response after a delay
          setCurrentMessageIndex(1);
          setStreamingMessage(true);
          
          // Wait for protagonist to complete
          setTimeout(() => {
            setStreamingMessage(false);
            
            // Keep loading indicator visible between messages
            setTimeout(() => {
              // Show antagonist response after a delay
              setCurrentMessageIndex(2);
              setStreamingMessage(true);
              
              // Wait for antagonist to complete
              setTimeout(() => {
                setStreamingMessage(false);
                
                // Keep loading indicator visible between messages
                setTimeout(() => {
                  // Finally show the evaluation
                  setCurrentMessageIndex(3);
                  setStreamingMessage(true);
                  
                  setTimeout(() => {
                    setStreamingMessage(false);
                    setShowLoadingIndicator(false); // Hide loading when demo completes
                  }, 5000); // Time for evaluator to finish
                }, 4000);
              }, 5000); // Time for antagonist to speak
            }, 4000);
          }, 5000); // Time for protagonist to speak
        }, 4000);
      }, 1000); // Time for user message to be seen
    };
    
    startDemo();
  }, []);

  // Helper function to get message styling based on role (similar to ChatInterface)
  const getMessageStyle = (role) => {
    switch (role) {
      case 'user':
        return 'mx-auto max-w-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-white'
      case 'protagonist':
        return 'ml-auto max-w-3xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
      case 'antagonist':
        return 'mr-auto max-w-3xl bg-gradient-to-r from-red-500 to-orange-600 text-white'
      case 'evaluator':
        return 'mx-auto w-full max-w-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white'
      default:
        return 'mx-auto bg-gray-800/50 text-gray-200'
    }
  }

  // Helper component to display message icons
  const MessageIcon = ({ role }) => {
    switch (role) {
      case 'user':
        return <div className="bg-purple-500 rounded-full p-2"><User className="w-4 h-4 text-white" /></div>
      case 'protagonist':
        return <img src="/protagonist.png" alt="Protagonist" className="w-8 h-8 rounded-full" />
      case 'antagonist':
        return <img src="/antagonist.png" alt="Antagonist" className="w-8 h-8 rounded-full" />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <header className="fixed top-0 w-full bg-black/50 backdrop-blur-md border-b border-gray-800 z-10">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text">
            Verdyct
          </div>
          <div className="space-x-4">
            <button 
              onClick={() => navigate('/about')}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              About
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Log in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white hover:opacity-90 transition-opacity"
            >
              Sign up
            </button>
          </div>
        </nav>
      </header>
      
      <main className="container mx-auto px-4 pt-32 pb-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 text-transparent bg-clip-text">
            AI Verdycts that Reveal the Truth
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto space-y-4">
            <span className="block mb-4">
              Experience the world's first AI platform where two intelligent agents debate both sides of your question until they reach a verdyct.
            </span>
            <span className="block text-lg">
              ⚔️ Watch as two AI agents argue pros and cons
              • 🔄 See perspectives you might have missed
              • 🎯 Get unbiased, well-reasoned answers
            </span>
          </p>
          
          {/* Add a CTA button right after the intro text for more visibility */}
          <div className="mt-8">
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-lg font-semibold text-white hover:opacity-90 transition-opacity"
            >
              Start Your First Debate
            </button>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
          <div className="bg-gray-800/30 rounded-xl p-6">
            <div className="text-purple-400 text-3xl mb-4">⚖️</div>
            <h3 className="text-xl font-semibold text-white mb-2">Balanced Perspectives</h3>
            <p className="text-gray-400">No more AI yes-men. Our agents actively challenge each other to uncover all angles of any topic.</p>
          </div>
          <div className="bg-gray-800/30 rounded-xl p-6">
            <div className="text-purple-400 text-3xl mb-4">🎭</div>
            <h3 className="text-xl font-semibold text-white mb-2">Dual Agent Technology</h3>
            <p className="text-gray-400">Two AI minds are better than one. Watch them debate until they reach a solid conclusion or agreement.</p>
          </div>
          <div className="bg-gray-800/30 rounded-xl p-6">
            <div className="text-purple-400 text-3xl mb-4">💡</div>
            <h3 className="text-xl font-semibold text-white mb-2">Better Decisions</h3>
            <p className="text-gray-400">Make informed choices with comprehensive arguments from both sides of any discussion.</p>
          </div>
        </div>
        
        {/* Updated demo section with ChatInterface-like styling */}
        <div className="max-w-4xl mx-auto mb-16 overflow-hidden">
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 backdrop-blur-sm p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            
            <AnimatePresence mode="popLayout">
              {/* Display messages that have been shown so far */}
              {demoMessages.slice(0, currentMessageIndex + 1).map((message, index) => {
                const isLastMessage = index === currentMessageIndex;
                return (
                  <motion.div
                    key={`message-${message.role}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`mb-6 flex ${
                      message.role === 'evaluator' || message.role === 'user'
                        ? 'justify-center w-full' 
                        : message.role === 'antagonist' 
                          ? 'justify-start' 
                          : 'justify-end'
                    }`}
                  >
                    <div className={`flex items-end space-x-2 ${
                      message.role === 'evaluator' ? 'w-full max-w-2xl' : ''
                    }`}>
                      {(message.role === 'antagonist' || message.role === 'user') && (
                        <MessageIcon role={message.role} />
                      )}
                      <div
                        className={`rounded-2xl px-6 py-4 ${getMessageStyle(message.role)} ${
                          message.role === 'evaluator' ? 'text-center my-8' : ''
                        }`}
                      >
                        {message.role === 'evaluator' && (
                          <div className="font-semibold mb-2">Final Evaluation</div>
                        )}
                        <MessageContent 
                          content={message.content} 
                          shouldStream={isLastMessage && streamingMessage} 
                        />
                      </div>
                      {message.role === 'protagonist' && (
                        <MessageIcon role={message.role} />
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* Show loading animation whenever we're between messages or waiting */}
              {showLoadingIndicator && !streamingMessage && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex justify-start mb-6"
                >
                  <div className="bg-gray-800/50 rounded-2xl px-6 py-4">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Add an additional CTA after the demo */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <p className="text-xl text-gray-300 mb-6">
            Witness AI debating both sides of any question, providing deep insights to help you find the truth.
          </p>
        </div>
        
        <div className="mt-16 text-center">
          <button
            onClick={() => navigate('/signup')}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-xl font-semibold text-white hover:opacity-90 transition-opacity"
          >
            Start Your First Debate
          </button>
          <p className="mt-4 text-gray-400">{monthlyFreeDebates} free debates monthly • No credit card required</p>
          <p className="mt-2 text-sm text-gray-500">Join thousands of users making better decisions with AI debates</p>
          <p className="mt-4 text-sm text-gray-500">
            By using our service, you agree to our{' '}
            <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline">
              Terms of Service
            </Link>{' '}
            and acknowledge that you have read our{' '}
            <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}